import { NaiveDate, YearMonth } from "@tento-chrono";
import { RowNetworkKey } from "@tento-lona/sheets";

/**
 * One day's weather read from a location's shared weather row: the canonical
 * `RowNetworkKey.weatherDataKey` row the sheet's weather cells bind (the home
 * card's accessory shows it beside the grid).
 */
export namespace WeatherDay {
  export type Day = { avgtempF: number; condition: { code: number; text: string } };

  /** Fetches `rowKey`'s cells in `[start, end)`; resolves to the wire cells. */
  export type Get = (rowKey: RowNetworkKey, start: NaiveDate, end: NaiveDate) => Promise<unknown>;

  /**
   * Reads `date`'s month of location `lid`'s weather row and returns `date`'s
   * day, or null when the row has no cell for it or the reply is not a list.
   */
  export async function read(get: Get, lid: number, date: NaiveDate): Promise<Option<Day>> {
    const start = NaiveDate.fromYmd1Exp(date.yr, date.mth, 1);
    const end = start.addDays(YearMonth.daysInMonth({ yr: date.yr, mth: date.mth }));
    const cells = await get(RowNetworkKey.fromWire(RowNetworkKey.weatherDataKey(String(lid))), start, end);
    return dayOf(cells, date);
  }

  /**
   * `date`'s day among a weather row's wire cells, or null when no cell is
   * that day's or the reply is not a list. A weather cell's data names its
   * day as an ISO date string ("2026-10-07").
   */
  export function dayOf<D = Day>(cells: unknown, date: NaiveDate): Option<D> {
    if (!Array.isArray(cells)) return null;
    const day = date.toString();
    for (const cell of cells) {
      const data = cell?.data;
      if (data?.date === day) return data.day ?? null;
    }
    return null;
  }
}
